import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://www.fairplayed.app';
const DEFAULT_IMAGE = `${SITE_URL}/images/og-fourball.jpg`;

const publicPages: Record<string, { title: string; description: string }> = {
  '/': {
    title: 'Social Golf Betting App for Fourballs | FairPlay',
    description: 'Make your golf fourball more fun with handicap-fair bets, side games, shared scoring and clear payouts. Built for South African golfers.',
  },
  '/how-it-works': {
    title: 'How FairPlay Works | Social Golf Betting App',
    description: 'See how FairPlay helps a fourball agree the course, format and stake, keep score together and settle the round clearly.',
  },
  '/golf-side-bets': {
    title: 'Golf Side Bets for Your Fourball | FairPlay',
    description: 'Simple golf side-bet ideas for a social fourball, including nearest the pin, longest drive and match-play challenges.',
  },
  '/privacy': {
    title: 'Privacy Policy | FairPlay',
    description: 'How FairPlay collects, uses and protects personal information for golfers using the service.',
  },
  '/terms': {
    title: 'Terms of Service | FairPlay',
    description: 'The terms that govern access to and use of the FairPlay social golf platform.',
  },
};

const appPages: Array<{ match: RegExp; title: string; description: string }> = [
  { match: /^\/login$/, title: 'Sign In | FairPlay', description: 'Sign in to your FairPlay golf account.' },
  { match: /^\/register$/, title: 'Create an Account | FairPlay', description: 'Create your FairPlay golf account.' },
  { match: /^\/forgot-password$/, title: 'Reset Password | FairPlay', description: 'Request a FairPlay password reset.' },
  { match: /^\/reset-password$/, title: 'Choose a New Password | FairPlay', description: 'Choose a new password for your FairPlay account.' },
  { match: /^\/verify-email$/, title: 'Verify Email | FairPlay', description: 'Verify the email address for your FairPlay account.' },
  { match: /^\/join\//, title: 'Join a Golf Game | FairPlay', description: 'Open a private FairPlay golf-game invitation.' },
  { match: /^\/game(?:\/|$)/, title: 'My Golf Games | FairPlay', description: 'View and score your FairPlay golf games.' },
  { match: /^\/quick-game$/, title: 'Create a Golf Game | FairPlay', description: 'Set up a round for your fourball.' },
  { match: /^\/wallet$/, title: 'Wallet | FairPlay', description: 'View your FairPlay wallet and transaction activity.' },
  { match: /^\/profile$/, title: 'Player Profile | FairPlay', description: 'Manage your FairPlay golfer profile.' },
  { match: /^\/golfers(?:\/|$)/, title: 'Golfers | FairPlay', description: 'Find golfers in your FairPlay circle.' },
  { match: /^\/messages(?:\/|$)/, title: 'Messages | FairPlay', description: 'View your FairPlay conversations.' },
  { match: /^\/notifications$/, title: 'Notifications | FairPlay', description: 'View your FairPlay invitations and updates.' },
  { match: /^\/admin(?:\/|$)/, title: 'Administration | FairPlay', description: 'FairPlay administration.' },
  { match: /^\/(?:pay\/sandbox|deposit\/)/, title: 'Payment | FairPlay', description: 'Complete a FairPlay payment action.' },
];

const upsertMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element!.setAttribute(name, value));
};

export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const cleanPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    const page = publicPages[cleanPath];
    const appPage = appPages.find(({ match }) => match.test(cleanPath));
    const indexable = Boolean(page);
    const title = page?.title || appPage?.title || 'Page Not Found | FairPlay';
    const description = page?.description || appPage?.description || 'The requested FairPlay page could not be found.';

    document.title = title;
    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: indexable ? 'index, follow, max-image-preview:large' : 'noindex, follow',
    });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: `${SITE_URL}${cleanPath}` });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: DEFAULT_IMAGE });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: DEFAULT_IMAGE });

    const existingCanonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (indexable) {
      const canonical = existingCanonical || document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', `${SITE_URL}${cleanPath === '/' ? '/' : cleanPath}`);
      if (!existingCanonical) document.head.appendChild(canonical);
    } else {
      existingCanonical?.remove();
    }
  }, [pathname]);

  return null;
}
